import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from "react";

interface OpenSuggestionPanelContextValue {
  openSuggestionId: number | null;
  toggleSuggestionPanel: (suggestionId: number) => void;
  closeSuggestionPanel: () => void;
}

interface OpenSuggestionPanelProviderProps {
  children: ReactNode;
}

const OpenSuggestionPanelContext = createContext<OpenSuggestionPanelContextValue | undefined>(
  undefined
);

const OpenSuggestionPanelProvider = ({ children }: OpenSuggestionPanelProviderProps) => {
  const [openSuggestionId, setOpenSuggestionId] = useState<number | null>(null);

  const toggleSuggestionPanel = useCallback((suggestionId: number) => {
    setOpenSuggestionId((prev) => (prev === suggestionId ? null : suggestionId));
  }, []);

  const closeSuggestionPanel = useCallback(() => {
    setOpenSuggestionId(null);
  }, []);

  const value = useMemo<OpenSuggestionPanelContextValue>(
    () => ({ openSuggestionId, toggleSuggestionPanel, closeSuggestionPanel }),
    [openSuggestionId, toggleSuggestionPanel, closeSuggestionPanel]
  );

  return (
    <OpenSuggestionPanelContext.Provider value={value}>
      {children}
    </OpenSuggestionPanelContext.Provider>
  );
};

const useOpenSuggestionPanel = () => {
  const context = useContext(OpenSuggestionPanelContext);
  if (context === undefined) {
    throw new Error("useOpenSuggestionPanel can only be used in a OpenSuggestionPanelProvider tree");
  }
  return context;
};

export { OpenSuggestionPanelProvider, OpenSuggestionPanelContext, useOpenSuggestionPanel };
